import type { ApiResponse } from '@weather-story/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeader(): Record<string, string> {
    // Get token from localStorage (zustand persist)
    const stored = localStorage.getItem('weather-story-auth');
    if (stored) {
      const { state } = JSON.parse(stored);
      if (state?.accessToken) {
        return { Authorization: `Bearer ${state.accessToken}` };
      }
    }
    return {};
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...(options.headers as Record<string, string>),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Request failed');
    }

    return data;
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';
    return this.request<T>(endpoint + queryString);
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('image', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Upload failed');
    }

    return data;
  }
}

export const api = new ApiClient(API_BASE_URL);

// Adapter for shared-hooks ApiClient interface (catches throws, returns { error })
export const apiClientAdapter: import('@weather-story/shared-types').ApiClient = {
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>) {
    try {
      const stringParams = params
        ? Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          )
        : undefined;
      const response = await api.get<T>(endpoint, stringParams);
      return { data: response.data };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
  async post<T>(endpoint: string, body?: unknown) {
    try {
      const response = await api.post<T>(endpoint, body);
      return { data: response.data };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
  async put<T>(endpoint: string, body?: unknown) {
    try {
      const response = await api.patch<T>(endpoint, body);
      return { data: response.data };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
  async delete<T>(endpoint: string) {
    try {
      const response = await api.delete<T>(endpoint);
      return { data: response.data };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
};
